import React, { useState, useEffect } from 'react';
import { RiFileWarningLine } from 'react-icons/ri';

type CampaignTableProps = {
  campaigntableData: {
    fromMonth: string;
    toMonth: string;
    rows: Array<{
      month: string;
      campaigns: number;
      emailsSent: number;
      opened: { count: number; rate: number };
      clicked: number;
      unsubscribed: number;
      complaints: number;
    }>;
  };
  timeRange?: string;
  onTimeRangeChange?: (range: string) => void;
};

const RiFileWarningLineComponent =
  RiFileWarningLine as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;

const CampaignTable: React.FC<CampaignTableProps> = ({
  campaigntableData,
  timeRange = '1',
  onTimeRangeChange
}) => {
  // Local state for dropdown if parent doesn't control it
  const [localTimeRange, setLocalTimeRange] = useState(timeRange);

  // Update local state when prop changes
  useEffect(() => {
    setLocalTimeRange(timeRange);
  }, [timeRange]);

  const { fromMonth, toMonth, rows = [] } = campaigntableData || {};
  const hasData = rows.length > 0;

  const formatMonth = (monthStr: string) => {
    if (!monthStr) return 'N/A';
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  // Handle dropdown change
  const handleRangeChange = (value: string) => {
    setLocalTimeRange(value);
    if (onTimeRangeChange) {
      onTimeRangeChange(value);
    }
  };

  // Get display label for current range
  // const getRangeLabel = (range: string) => {
  //   switch (range) {
  //     case '1': return 'Last 1 Month';
  //     case '6': return 'Last 6 Months';
  //     default: return 'Last 1 Month';
  //   }
  // };

  return (
    <div className='mt-6 bg-white p-4 rounded-lg border border-gray-200 shadow-sm'>
      <div className='flex justify-between items-center mb-4'>
        <h3
          className='text-lg font-semibold'
          style={{ color: '#3F5265', fontSize: '20px' }}
        >
          Campaign Table
        </h3>

        <div className="flex items-center gap-4">
          {fromMonth && toMonth && (
            <p className='text-sm text-gray-500 font-medium'>
              {formatMonth(fromMonth)} — {formatMonth(toMonth)}
            </p>
          )}

          <select
            className='border rounded p-2'
            style={{
              borderColor: '#00A4BD',
              color: '#00A4BD',
              borderWidth: '2px',
            }}
            value={localTimeRange}
            onChange={(e) => handleRangeChange(e.target.value)}
          >
            <option value='1'>Last 1 Month</option>
            <option value='6'>Last 6 Months</option>
          </select>
        </div>
      </div>

      <table className='w-full text-left border-collapse border border-gray-200'>
        <thead style={{ backgroundColor: '#F5F8FA' }}>
          <tr className='border-b' style={{ color: '#657584', fontSize: '12px' }}>
            <th className='py-4 pr-4 text-left font-normal pl-3'>MONTH</th>
            <th className='pr-4 text-left font-normal pl-3'>CAMPAIGNS</th>
            <th className='pr-4 text-left font-normal pl-3'>EMAIL SENT</th>
            <th className='pr-4 text-left font-normal pl-3'>OPENED</th>
            <th className='pr-4 text-left font-normal pl-3'>CLICKED</th>
            <th className='pr-4 text-left font-normal pl-3'>UNSUBSCRIBED</th>
            <th className='pr-4 text-left font-normal pl-3'>SPAM COMPLAINTS</th>
          </tr>
        </thead>

        <tbody style={{ color: '#3F5265', fontSize: '14px' }}>
          {hasData ? (
            rows.map((row, index) => (
              <tr key={index} className='border-b hover:bg-gray-50'>
                <td className='py-4 pr-4 pl-3 font-medium'>{formatMonth(row.month)}</td>
                <td className='pr-4 pl-3'>{row.campaigns}</td>
                <td className='pr-4 pl-3'>{row.emailsSent}</td>
                <td className='pr-4 pl-3'>
                  {row.opened?.rate || 0}% ({row.opened?.count || 0})
                </td>
                <td className='pr-4 pl-3'>{row.clicked}</td>
                <td className='pr-4 pl-3'>{row.unsubscribed}</td>
                <td className='pr-4 pl-3'>{row.complaints}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className='py-20 text-center'>
                <div className='flex flex-col items-center justify-center text-gray-500'>
                  <RiFileWarningLineComponent size={48} className='mb-6 text-gray-400' />
                  <p className='text-base mb-1'>Your campaign performance metrics will appear here.</p>
                  <p className='text-base mb-6'>Start by launching your first campaign.</p>
                  <button className='bg-[#6366F1] hover:bg-[#4F46E5] text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors'>
                    + Create Campaign
                  </button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CampaignTable;